<template>
  <div
    ref="wrap"
    class="artboards"
  >
    <canvas
      ref="canvas"
      class="artboards__canvas"
      @mousemove="onMove"
      @mouseleave="onLeave"
      @click="onClick"
    />

    <div
      v-if="tooltip.visible"
      class="artboards__tooltip"
      :style="tooltipStyle"
    >
      {{ tooltip.text }}
    </div>
  </div>
</template>

<script>
export default {
  name: "ArtboardsCanvas",

  props: {
    imageSrc: { type: String, required: true },
    artboards: { type: Array, required: true, default: () => [] },
    sourceSize: { type: Object, required: true },
    hoverFill: { type: String, default: "#198DBB7D" },
    visibleStroke: { type: String, default: "#209C86" },
    contrastStroke: { type: String, default: "#FFFFFF" },
    strokeWidth: { type: Number, default: 2 },
    contrastWidth: { type: Number, default: 4 },
    checkColor: { type: String, default: "#209C86" },
    checkBg: { type: String, default: "white" },
  },
  emits: ["artboard-click", "artboard-hover"],

  data() {
    return {
      img: null,
      ctx: null,

      dpr: 1,
      drawnW: 0,
      drawnH: 0,

      scaleX: 1,
      scaleY: 1,

      hovered: -1,

      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        text: "",
      },

      ro: null,
    };
  },

  computed: {
    tooltipStyle() {
      return { left: this.tooltip.x + "px", top: this.tooltip.y + "px" };
    },

    vp() {
      return {
        left: 0,
        top: 0,
        width: this.sourceSize.width,
        height: this.sourceSize.height,
      };
    },
  },

  watch: {
    imageSrc: {
      immediate: true,
      handler() {
        this.loadImage();
      },
    },
    artboards: {
      deep: true,
      handler() {
        this.redraw();
      },
    },
    sourceSize: { 
      deep: true, 
      handler() { 
        this.resize(); 
      } 
    },
  },

  mounted() {
    this.dpr = window.devicePixelRatio || 1;

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this.$refs.wrap);
    } else {
      window.addEventListener("resize", this.resize);
    }
  },
  
  beforeUnmount() {
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener("resize", this.resize);
  },

  methods: {
    async loadImage() {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = this.imageSrc;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      this.img = img;
      this.resize();
    },

    resize() {
      if (!this.img) return;

      const canvas = this.$refs.canvas;
      const wrap = this.$refs.wrap;
      this.ctx = canvas.getContext("2d");

      const { width: containerW, height: containerH } = wrap.getBoundingClientRect();
      if (!containerW || !containerH) return;

      const { w: canvasW, h: canvasH, left, top } = this.fitCanvasToContainerContain(
        containerW,
        containerH,
        this.img.naturalWidth,
        this.img.naturalHeight
      );

      canvas.style.position = "absolute";
      canvas.style.width = `${canvasW / canvas.currentCSSZoom}px`;
      canvas.style.height = `${canvasH / canvas.currentCSSZoom}px`;
      canvas.style.left = `${left / canvas.currentCSSZoom}px`;
      canvas.style.top = `${top / canvas.currentCSSZoom}px`;

      this.drawnW = canvasW;
      this.drawnH = canvasH;

      this.dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(canvasW * this.dpr);
      canvas.height = Math.round(canvasH * this.dpr);

      this.scaleX = canvasW / this.vp.width;
      this.scaleY = canvasH / this.vp.height;

      this.redraw();
    },

    mapArtboardCoordsToLocalCoords(board) {
      return {
        x: (board.left - this.vp.left) * this.scaleX,
        y: (board.top - this.vp.top) * this.scaleY,
        w: board.width * this.scaleX,
        h: board.height * this.scaleY,
      };
    },

    insetRectForStroke(r, lineWidth) {
      const half = lineWidth / 2;
      return {
        x: r.x + half,
        y: r.y + half,
        w: Math.max(0, r.w - lineWidth),
        h: Math.max(0, r.h - lineWidth),
      };
    },

    prepareContext() {
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.ctx.clearRect(0, 0, this.drawnW, this.drawnH);
    },

    drawBackground() {
      this.ctx.drawImage(this.img, 0, 0, this.drawnW, this.drawnH);
    },

    drawDoubleStrokeRect(r0, color) {
      const outer = this.insetRectForStroke(r0, this.contrastWidth);
      this.ctx.lineWidth = this.contrastWidth;
      this.ctx.strokeStyle = this.contrastStroke;
      this.ctx.strokeRect(outer.x, outer.y, outer.w, outer.h);

      const inner = this.insetRectForStroke(r0, this.strokeWidth);
      this.ctx.lineWidth = this.strokeWidth;
      this.ctx.strokeStyle = color;
      this.ctx.strokeRect(inner.x, inner.y, inner.w, inner.h);
    },

    redraw() {
      if (!this.img || !this.ctx) return;
      
      this.prepareContext();
      this.drawBackground();
      this.drawVisibleOverlays();
      this.drawHoverOverlay();
    },

    drawVisibleOverlays() {
      for (const b of this.artboards) {
        if (!b?.visible) continue;

        const r0 = this.mapArtboardCoordsToLocalCoords(b);

        this.ctx.save();
        this.drawDoubleStrokeRect(r0, this.visibleStroke);
        this.ctx.restore();
      }
    },

    drawHoverOverlay() {
      if (this.hovered < 0 || this.hovered >= this.artboards.length) return;

      const b = this.artboards[this.hovered];
      const r0 = this.mapArtboardCoordsToLocalCoords(b);

      this.ctx.save();

      this.ctx.fillStyle = this.hoverFill;
      this.ctx.fillRect(r0.x, r0.y, r0.w, r0.h);

      this.ctx.restore();
    },

    mousePos(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },

    hit(cssX, cssY) {
      for (let i = this.artboards.length - 1; i >= 0; i--) {
        const r = this.mapArtboardCoordsToLocalCoords(this.artboards[i]);
        if (
          cssX >= r.x &&
          cssX <= r.x + r.w &&
          cssY >= r.y &&
          cssY <= r.y + r.h
        ) {
          return i;
        }
      }
      return -1;
    },

    onMove(e) {
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const wrapRect = this.$refs.wrap.getBoundingClientRect();

      const mouseX = e.clientX - canvasRect.left;
      const mouseY = e.clientY - canvasRect.top;

      const idx = this.hit(mouseX, mouseY);

      if (idx !== this.hovered) {
        this.hovered = idx;
        this.redraw();

        if (idx >= 0) {
          this.tooltip.visible = true;
          this.tooltip.text = this.artboards[idx].name || "Artboard";
        } else {
          this.tooltip.visible = false;
        }
      }

      if (!this.tooltip.visible) return;

      this.tooltip.x = e.clientX - wrapRect.left + 14;
      this.tooltip.y = e.clientY - wrapRect.top - 28;
    },

    onLeave() {
      this.hovered = -1;
      this.tooltip.visible = false;
      this.redraw();
      this.$emit("artboard-hover", null);
    },

    onClick(e) {
      const { x, y } = this.mousePos(e);
      const idx = this.hit(x, y);
      if (idx >= 0) this.$emit("artboard-click", this.artboards[idx]);
    },
    fitCanvasToContainerContain(containerW, containerH, sourceW, sourceH) {
      if (!containerW || !containerH || !sourceW || !sourceH) {
        return { w: 0, h: 0, left: 0, top: 0 };
      }

      const srcRatio = sourceW / sourceH;
      const ctrRatio = containerW / containerH;

      let w, h;

      if (ctrRatio > srcRatio) {
        h = containerH;
        w = h * srcRatio;
      } else {
        w = containerW;
        h = w / srcRatio;
      }

      return {
        w,
        h,
        left: (containerW - w) / 2,
        top: (containerH - h) / 2,
      };
    }
  },
};
</script>

<style scoped>
.artboards {
  position: relative;
  width: 100%;
  height: 100%;
}

.artboards__canvas {
  display: block;
  cursor: pointer;
}

.artboards__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  white-space: nowrap;
  z-index: 5;
}
</style>
